import { defineMessages } from '@edx/frontend-platform/i18n';

const messages = defineMessages({
  // PurchasePanel
  removeFromCart: {
    id: 'discover.courseDetail.removeFromCart',
    defaultMessage: 'Remove from cart',
    description: 'Button label to remove the course from the cart',
  },
  addToCart: {
    id: 'discover.courseDetail.addToCart',
    defaultMessage: 'Add to cart',
    description: 'Button label to add the course to the cart',
  },
  enrollNow: {
    id: 'discover.courseDetail.enrollNow',
    defaultMessage: 'Enroll Now',
    description: 'Button label to enroll directly (used when cart flow is disabled)',
  },
  buyNow: {
    id: 'discover.courseDetail.buyNow',
    defaultMessage: 'Buy Now',
    description: 'Button label to purchase a paid course directly when the cart flow is disabled',
  },
  newBadge: {
    id: 'discover.courseDetail.newBadge',
    defaultMessage: 'New',
    description: 'Badge indicating a recently added course',
  },
  chooseModeLabel: {
    id: 'discover.courseDetail.chooseModeLabel',
    defaultMessage: 'CHOOSE ENROLLMENT PLAN',
    description: 'Label preceding the course enrollment mode selector',
  },
  chooseIntervalLabel: {
    id: 'discover.courseDetail.chooseIntervalLabel',
    defaultMessage: 'Billing interval',
    description: 'Accessible group label for the monthly/yearly billing interval toggle',
  },
  intervalMonthly: {
    id: 'discover.courseDetail.subscribe.intervalMonthly',
    defaultMessage: 'Monthly',
    description: 'Name of the monthly billing option in the interval selector',
  },
  intervalYearly: {
    id: 'discover.courseDetail.subscribe.intervalYearly',
    defaultMessage: 'Yearly',
    description: 'Name of the yearly billing option in the interval selector',
  },
  intervalSave: {
    id: 'discover.courseDetail.subscribe.intervalSave',
    defaultMessage: 'Save {percent}%',
    description: 'Badge on the yearly billing option showing the discount versus paying monthly for a year',
  },
  freeMode: {
    id: 'discover.courseDetail.freeMode',
    defaultMessage: 'Free',
    description: 'Label shown when a course mode has no price',
  },
  alreadyEnrolled: {
    id: 'discover.courseDetail.alreadyEnrolled',
    defaultMessage: 'Already enrolled',
    description: 'Button label shown when the learner is already enrolled in the course',
  },
  upgradeModeLabel: {
    id: 'discover.courseDetail.upgradeModeLabel',
    defaultMessage: 'UPGRADE YOUR PLAN',
    description: 'Label preceding the track selector when the learner is already enrolled and can upgrade',
  },
  currentPlan: {
    id: 'discover.courseDetail.currentPlan',
    defaultMessage: 'Your current plan: {mode}',
    description: 'Line showing which enrollment track the learner already holds',
  },
  upgradeNow: {
    id: 'discover.courseDetail.upgradeNow',
    defaultMessage: 'Upgrade Now',
    description: 'Button label to buy an upgrade to a higher track directly when the cart flow is disabled',
  },
  certificateBadge: {
    id: 'discover.courseDetail.certificateBadge',
    defaultMessage: 'Certificate',
    description: 'Badge on an enrollment track that earns a certificate',
  },
  enrollmentUnavailable: {
    id: 'discover.courseDetail.enrollmentUnavailable',
    defaultMessage: 'Enrollment unavailable',
    description: 'Button label when every enrollment track is hidden or has closed',
  },
  upgradePriceHint: {
    id: 'discover.courseDetail.upgradePriceHint',
    defaultMessage: 'Upgrade price — what you paid for {mode} is already deducted',
    description: 'Explains that the upgrade price shown is the difference against the track the learner holds',
  },
  addToWishlist: {
    id: 'discover.courseDetail.addToWishlist',
    defaultMessage: 'Add to wishlist',
    description: 'Accessible label for the icon button that adds the course to the wishlist',
  },
  removeFromWishlist: {
    id: 'discover.courseDetail.removeFromWishlist',
    defaultMessage: 'Remove from wishlist',
    description: 'Accessible label for the icon button that removes the course from the wishlist',
  },

  // CourseReviews
  starsAriaLabel: {
    id: 'discover.courseDetail.reviews.starsAriaLabel',
    defaultMessage: '{value} out of 5',
    description: 'Accessible label describing a star rating value out of 5',
  },
  courseRatingHeading: {
    id: 'discover.courseDetail.reviews.ratingHeading',
    defaultMessage: 'Course Rating',
    description: 'Heading for the course rating summary section',
  },
  outOfFive: {
    id: 'discover.courseDetail.reviews.outOfFive',
    defaultMessage: 'out of 5',
    description: 'Suffix after the average rating value indicating the maximum',
  },
  totalRatings: {
    id: 'discover.courseDetail.reviews.totalRatings',
    defaultMessage: '{count} Ratings',
    description: 'Label showing the total number of ratings for the course',
  },
  starRowLabel: {
    id: 'discover.courseDetail.reviews.starRowLabel',
    defaultMessage: '{count} star',
    description: 'Label for a row in the rating distribution (e.g., "5 star")',
  },
  learnerFallbackName: {
    id: 'discover.courseDetail.reviews.learnerFallbackName',
    defaultMessage: 'Learner {id}',
    description: 'Fallback display name for a reviewer when no username is available',
  },
  courseReviewsHeading: {
    id: 'discover.courseDetail.reviews.heading',
    defaultMessage: 'Course Reviews',
    description: 'Heading for the course reviews section',
  },

  // AboutSection
  aboutHeading: {
    id: 'discover.courseDetail.about.heading',
    defaultMessage: 'About the course',
    description: 'Heading for the course about section',
  },
  aboutSeeMore: {
    id: 'discover.courseDetail.about.seeMore',
    defaultMessage: 'See more',
    description: 'Button label to expand the truncated course description',
  },
  aboutSeeLess: {
    id: 'discover.courseDetail.about.seeLess',
    defaultMessage: 'See less',
    description: 'Button label to collapse the expanded course description',
  },

  // CourseDetail page
  loadingCourse: {
    id: 'discover.courseDetail.loading',
    defaultMessage: 'Loading course',
    description: 'Screen reader text shown while a course detail page is loading',
  },
  courseLoadError: {
    id: 'discover.courseDetail.loadError',
    defaultMessage: 'Unable to load this course. Please try again later.',
    description: 'Error message shown when a course detail page fails to load',
  },
  goBack: {
    id: 'discover.courseDetail.goBack',
    defaultMessage: 'Go back',
    description: 'Label for the back button on the course/program detail page',
  },
  untitledCourse: {
    id: 'discover.courseDetail.untitledCourse',
    defaultMessage: 'Untitled course',
    description: 'Fallback title shown when a course has no display name',
  },

  // ProgramDetail page
  loadingProgram: {
    id: 'discover.programDetail.loading',
    defaultMessage: 'Loading program',
    description: 'Screen reader text shown while a program detail page is loading',
  },
  programLoadError: {
    id: 'discover.programDetail.loadError',
    defaultMessage: 'Unable to load this program. Please try again later.',
    description: 'Error message shown when a program detail page fails to load',
  },
  untitledProgram: {
    id: 'discover.programDetail.untitledProgram',
    defaultMessage: 'Untitled program',
    description: 'Fallback title shown when a program has no name',
  },
  programDescriptionTitle: {
    id: 'discover.programDetail.descriptionTitle',
    defaultMessage: 'Description',
    description: 'Section heading for the program description',
  },
  programAboutTitle: {
    id: 'discover.programDetail.aboutTitle',
    defaultMessage: 'About',
    description: 'Section heading for the program about text',
  },
  programCoursesTitle: {
    id: 'discover.programDetail.coursesTitle',
    defaultMessage: 'Courses in this program',
    description: 'Section heading for the list of courses included in a program',
  },

  // CourseOutline
  outlineHeading: {
    id: 'discover.courseDetail.outline.heading',
    defaultMessage: 'Course Outline',
    description: 'Heading for the course outline section',
  },
  subscribeCta: {
    id: 'discover.courseDetail.subscribe.cta',
    defaultMessage: 'Subscribe · {price} / {interval}',
    description: 'Subscribe button showing recurring price and interval',
  },
  subscribed: {
    id: 'discover.courseDetail.subscribe.subscribed',
    defaultMessage: 'Subscribed',
    description: 'Badge shown when the learner has an active subscription',
  },
  orSubscribe: {
    id: 'discover.courseDetail.subscribe.orSubscribe',
    defaultMessage: 'Or subscribe',
    description: 'Divider label introducing the subscription option as an alternative to buying once',
  },
  inviteOnlyNotice: {
    id: 'discover.courseDetail.inviteOnlyNotice',
    defaultMessage: 'Invite only — ask your administrator to add you.',
    description: 'Shown instead of a price/purchase button when the product is invite only, meaning only staff can enroll learners',
  },
  manageBilling: {
    id: 'discover.courseDetail.subscribe.manageBilling',
    defaultMessage: 'Manage billing',
    description: 'Button opening the Stripe billing portal to update card or cancel',
  },
  perMonth: {
    id: 'discover.courseDetail.subscribe.perMonth',
    defaultMessage: 'Month',
    description: 'Billing interval word for a monthly subscription',
  },
  perYear: {
    id: 'discover.courseDetail.subscribe.perYear',
    defaultMessage: 'Year',
    description: 'Billing interval word for a yearly subscription',
  },
});

export default messages;
